// S-063-2 Copilot カレンダー要約パーサー（段階1 正規表現のみ）
//
// Microsoft Copilot の予定要約をユーザーが手動コピペした生テキストを 1 行ずつ解析し、
// 会社名 + 部署 + 担当者名 + 日時（JST 固定）を抽出して中間表現 ParsedEvent[] にする。
//
// 設計書: _management/designs/s063_2_calendar_import_ui_parser_2026-06-18.md
// 確定スコープ（けんじ 2026-06-18）: 段階1（正規表現）のみ。段階2 Gemini 補助は採用しない。
//
// 原則:
// - C1 でっち上げ禁止: 抽出失敗フィールドは ""（文字列）/ null（時刻）を明示的に返す。推測で埋めない。
// - C4 JST 固定: 時刻には必ず +09:00 を付ける。ナイーブ時刻・Z 禁止（meeting.recordedAt と桁を揃える）。
// - C7 別フィーチャー: メール / 電話は抽出対象にしない。tag/meetingTitle/recurrence は中間表現に留め
//   Firestore へは書かない（calendarEventsRepo 側で 7 フィールドのみ書込み）。

export interface ParsedEvent {
  companyName: string; // 正規化済み。失敗時 ""
  department: string; // 失敗時 ""
  contactName: string; // 失敗時 ""
  dateIso: string; // この行が属する開始日（YYYY-MM-DD / JST）。複数日一括取込で行ごとに保持。
  startTime: string | null; // ISO 8601 +09:00。失敗時 null
  endTime: string | null; // ISO 8601 +09:00。null = duration 補填対象
  // --- 以下は中間専用。Firestore 非保存 ---
  tag: string | null; // 'WEB'|'訪問'|'TEL'|'対面'|null
  meetingTitle: string | null; // 議題。マッチ補助にも使わない（UI 表示ヒントのみ）
  recurrence: string | null; // '毎週'|'毎月'|null。UI 表示ヒントのみ
  rawLine: string; // 元の 1 行（編集 UI 用）
  needsReview: boolean; // 抽出失敗あり → ユーザー確認 UI へ
}

export interface ParsedCalendarResult {
  events: ParsedEvent[];
  parseErrors: { line: string; reason: string }[]; // 完全失敗行（会社名も時刻も取れない）
  geminiAssisted: boolean; // 段階1 のみのため常に false
}

// ─── 正規表現定数（§3.4） ───

// タグ（行頭）。【WEB】/ [訪問] など。
const RE_TAG = /^\s*[【[]\s*(WEB|訪問|TEL|対面|電話|来社|外出)\s*[】\]]/u;

// 繰返し注記（行末）。※毎週 / （毎月）等。
const RE_RECUR = /[※(（]\s*(毎週|毎月|毎日|定期|隔週)[^)）]*[)）]?\s*$/u;

// 時刻範囲（括弧内 or 裸）。全角コロン・「時」・区切りゆれを normalizeTimeChars で吸収済みの前提。
//   14:00-15:00 / 14:00~15:00 / 14時-15時 / 14:00から15:00
const RE_TIME =
  /(\d{1,2})\s*[：:時]\s*(\d{0,2})\s*(?:[-~〜ー－–]|から)\s*(\d{1,2})\s*[：:時]\s*(\d{0,2})/u;

// 単一時刻（終了なし）。範囲が取れなかったときのフォールバック（例: （14:00））。
const RE_TIME_SINGLE = /(\d{1,2})\s*[：:時]\s*(\d{0,2})/u;

// 括弧（全角/半角）。時刻でない場合は議題候補。
const RE_PAREN = /[（(]([^）)]*)[）)]/u;

// 日本人氏名（本体末尾。姓名 2〜5 字。敬称は除去）。
//   全角スペース or 半角スペース区切りの末尾トークン。長音符 ー は含めない（議題カタカナ語との切り分け）。
const RE_NAME_TAIL = /[\s　]([一-龯ぁ-んァ-ヶ]{2,5})(?:\s*(?:さん|氏|様|君|ちゃん))?\s*$/u;

// 部署接尾辞（会社名と部署の分割点）。日本語接尾辞のみ（英語 Dept. 等は MVP 範囲外 / 設計 P0-2）。
//   ※ 「組」は除外する。大林組・鴻池組など建設会社名の末尾に頻出し、会社名を部署と誤抽出するため。
const RE_DEPT =
  /([^\s　]*?(?:本部|事業部|営業所|支店|センター|グループ|チーム|部|課|室|科|班|係))(?:[\s　]|$)/u;

// 部署接尾辞で終わるか（氏名候補のガード用）。「工事部」「設備部」を氏名と誤抽出しないため。
const RE_DEPT_SUFFIX_TAIL =
  /(?:本部|事業部|営業所|支店|センター|グループ|チーム|部|課|室|科|班|係)$/u;

// 議題語彙（確定リスト・§3.4）。氏名（2〜5 字の和名）との誤判定回避に使う。
const MEETING_TITLE_WORDS = [
  '報告', 'フォロー', '打合せ', '打ち合わせ', 'MTG', 'ミーティング', '定例',
  '朝礼', '昼礼', 'キックオフ', 'レビュー', '体験版', '相談', '確認',
  '会議', '商談', '提案', 'デモ', '説明', '見積', 'ヒアリング', '同行',
];
const RE_TITLE_WORDS = new RegExp(
  `(?:${MEETING_TITLE_WORDS.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
  'u',
);
const RE_KATAKANA_LONG = /[ァ-ヶー]{5,}/u; // 4 字超のカタカナ語
const RE_ALNUM_MIX = /[A-Za-z].*[0-9]|[0-9].*[A-Za-z]/u; // 英数字混在（K-D2 等）

// 会社名サフィックス除去。
const COMPANY_SUFFIX =
  /(様|御中|株式会社|（株）|\(株\)|㈱|有限会社|（有）|\(有\)|㈲|合同会社|Co\.,?\s*Ltd\.?|Inc\.?|Ltd\.?)/giu;

// 終了不明時に補填する既定会議長（分）。§4.2 / §7-4 けんじ確認事項（暫定 60 分）。
export const DEFAULT_MEETING_MINUTES = 60;

// 日跨ぎとして採用する最大スパン（分）。これ以上は録音切り忘れ等と判断し補填へ委ねる。
//   23:30-0:30（60 分）は採用、23:00-7:00（480 分）は不採用（テスト #13/#14）。
const MAX_SPAN_MIN = 8 * 60;

// 段階1 パース時の最大処理文字数（暴走入力ガード）。
//   複数日一括取込（1 ヶ月分 ≈ 30 日 × 10 予定 × 50 字 ≈ 15000 字）を見込んで余裕を持たせる。
export const MAX_COPILOT_RAW_CHARS = 32000;

// ─── ヘルパ ───

// 議題っぽいか（議題語彙 or 長カタカナ or 英数字混在）。
export function looksLikeMeetingTitle(s: string): boolean {
  const t = (s || '').trim();
  if (!t) return false;
  return RE_TITLE_WORDS.test(t) || RE_KATAKANA_LONG.test(t) || RE_ALNUM_MIX.test(t);
}

// 部署語っぽいか（氏名誤抽出ガード）。3 字以上で部署接尾辞終わりのみ部署とみなす。
//   ※ 2 字で接尾辞終わりの実在姓（阿部・服部・渡部・矢部・安部 等）を氏名として救うため
//     長さ 3 以上を条件にする（設備部・工事部・営業部 等の部署語は 3 字以上が大半）。
function looksLikeDept(s: string): boolean {
  const t = (s || '').trim();
  return t.length >= 3 && RE_DEPT_SUFFIX_TAIL.test(t);
}

// 役職・敬称（氏名トークン末尾から剥がす）。長い役職を先に並べる（本部長 → 部長 の誤一致回避）。
const RE_NAME_TITLE_TAIL =
  /(本部長|工場長|取締役|マネージャー|部長|課長|係長|次長|室長|所長|社長|専務|常務|主任|リーダー|ちゃん|さん|君|氏|様)$/u;

// 午前/午後（AM/PM）を 24 時間表記へ正規化する。午後12時=12:00 / 午前12時=00:00。
function normalizeAmPm(s: string): string {
  return s.replace(/(午前|午後)\s*(\d{1,2})\s*(?:時|:)\s*(\d{0,2})/gu, (_m, ap, hh, mm) => {
    let h = parseInt(hh, 10);
    if (ap === '午後') h = h < 12 ? h + 12 : h;
    else h = h === 12 ? 0 : h; // 午前
    return `${h}:${mm ? mm.padStart(2, '0') : '00'}`;
  });
}

// 全角コロン・全角数字・全角波ダッシュ・午前午後を時刻抽出前に正規化する。
//   ※ 行全体に 1 回だけかける（leftover 計算が一致するように）。
function normalizeTimeChars(s: string): string {
  const half = s
    .replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0))
    .replace(/：/g, ':')
    .replace(/[～〜]/g, '~'); // 全角波ダッシュ → 区切り
  return normalizeAmPm(half);
}

// 日付のみの見出し行（例:「2026年6月18日の予定」「6/18(木)」「2026/06/18」「6月18日」）。
//   会社/時刻を持たないため取込対象外。ただし複数日一括取込では「以降の行の基準日」を運ぶので、
//   単にスキップするのではなく日付（YYYY-MM-DD）を抽出して currentBaseDate を更新する（§11-3）。
//   行全体が日付見出しのときのみマッチさせる（先頭に日付を含むイベント行を誤認しないため）。
const RE_HEADER_YMD_KANJI = /^(?:(\d{4})年)?\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日$/u; // 2026年6月18日 / 6月18日
// 年あり ISO 形は区切り / . - を許す。年なしはスラッシュのみに限定する。
//   ※ 「10-12」「1.5」「6.18」のような裸の 2 数字を日付見出しと誤認させないため（区切り '-' '.' を年なしで使わない）。
const RE_HEADER_YMD_ISO = /^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/u; // 2026/06/18 / 2026-06-18 / 2026.06.18（年必須）
const RE_HEADER_YMD_SLASH = /^(\d{1,2})\/(\d{1,2})$/u; // 6/18（年なしはスラッシュのみ）

// 見出し行の装飾（全角数字・末尾の曜日括弧「(木)」・「の予定」）を取り除いた素の文字列を返す。
function stripHeaderDecorations(rawLine: string): string {
  let s = rawLine.replace(/[０-９]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0xfee0)).trim();
  s = s.replace(/の予定\s*$/u, '').trim();
  s = s.replace(/[（(][^)）]{0,3}[)）]\s*$/u, '').trim();
  return s;
}

// 見出しの生パーツ（年文字列・月・日）を取り出す。書式に合わなければ null（値域・実在は未検証）。
function matchHeaderParts(s: string): { yStr?: string; mo: number; da: number } | null {
  const mk = s.match(RE_HEADER_YMD_KANJI);
  if (mk) return { yStr: mk[1], mo: parseInt(mk[2], 10), da: parseInt(mk[3], 10) };
  const mi = s.match(RE_HEADER_YMD_ISO);
  if (mi) return { yStr: mi[1], mo: parseInt(mi[2], 10), da: parseInt(mi[3], 10) };
  const ms = s.match(RE_HEADER_YMD_SLASH);
  if (ms) return { yStr: undefined, mo: parseInt(ms[1], 10), da: parseInt(ms[2], 10) };
  return null;
}

// 行が「日付見出しの形」をしているか（値域・実在は問わない）。不正日付見出し（13月1日 等）を
//   疑似イベント化させず parseErrors へ隔離するために使う。
export function isDateHeaderShaped(rawLine: string): boolean {
  return matchHeaderParts(stripHeaderDecorations(rawLine)) !== null;
}

// 日付見出し行から開始日（YYYY-MM-DD）を抽出する。見出しでない／実在しない日付なら null。
//   - 年なし見出し（「6/18」「6月18日」）は fallbackDateIso（直前の基準日）の年を引き継ぐ。
//   - allowYearRoll=false（取込内の最初の見出し）では年を繰り上げない。最初の見出しの fallback は
//     ピッカー既定日（=今日になりうる）であって時系列上の前後関係が無いため、繰上げると年を捏造する（C1）。
//   - allowYearRoll=true（2 つ目以降の見出し）では、月が直前見出しより小さいとき翌年へ繰り上げる
//     （時系列前提の 12月→1月 折返しを 1 度だけ +1 年で吸収）。
//   - 実在しない日付（2/30・11/31・非閏 2/29 等）は採用しない（C1 でっち上げ禁止）。
export function extractDateFromHeader(
  rawLine: string,
  fallbackDateIso: string,
  allowYearRoll = true,
): string | null {
  const parts = matchHeaderParts(stripHeaderDecorations(rawLine));
  if (!parts) return null;
  const { yStr, mo, da } = parts;
  if (mo < 1 || mo > 12 || da < 1 || da > 31) return null; // 粗い値域チェック（早期棄却）

  let y: number;
  if (yStr) {
    y = parseInt(yStr, 10); // 明示年はそのまま採用（繰上げしない）
  } else {
    const fy = parseInt(fallbackDateIso.slice(0, 4), 10);
    const fmo = parseInt(fallbackDateIso.slice(5, 7), 10);
    // 年なし: 直前基準日の年を引き継ぎ、許可時かつ月が戻ったら（12→1 等）翌年へ折返す。
    y = allowYearRoll && mo < fmo ? fy + 1 : fy;
  }
  // 実在日チェック（年確定後に判定。非閏年の 2/29 等を正しく弾く）。Date.UTC の桁あふれ正規化を利用。
  const probe = new Date(Date.UTC(y, mo - 1, da));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== da) {
    return null;
  }
  return `${y}-${String(mo).padStart(2, '0')}-${String(da).padStart(2, '0')}`;
}

const pad = (x: string) => x.padStart(2, '0');

// ISO 文字列（+09:00 維持）の日付部のみ n 日進める。
function addDaysIso(iso: string, n: number): string {
  const datePart = iso.slice(0, 10); // YYYY-MM-DD
  const rest = iso.slice(10); // THH:MM:SS+09:00
  const d = new Date(datePart + 'T00:00:00Z'); // 日付演算は UTC で（TZ ずれ回避）
  d.setUTCDate(d.getUTCDate() + n);
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const da = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${da}${rest}`;
}

// 絶対時刻 Date を JST（+09:00）の ISO 文字列に整形する。new Date().toISOString() は Z になるため使わない。
export function toJstIso(d: Date): string {
  const jst = new Date(d.getTime() + 9 * 3600 * 1000);
  const y = jst.getUTCFullYear();
  const mo = String(jst.getUTCMonth() + 1).padStart(2, '0');
  const da = String(jst.getUTCDate()).padStart(2, '0');
  const h = String(jst.getUTCHours()).padStart(2, '0');
  const mi = String(jst.getUTCMinutes()).padStart(2, '0');
  const se = String(jst.getUTCSeconds()).padStart(2, '0');
  return `${y}-${mo}-${da}T${h}:${mi}:${se}+09:00`;
}

// +09:00 ISO 文字列に分を加算し、+09:00 を維持して返す（§4.2 endTime 補填の土台）。
export function addMinutesToJstIso(iso: string, mins: number): string {
  return toJstIso(new Date(Date.parse(iso) + mins * 60000));
}

// 保存用 endTime の最終決定（§3.6/§4.2 を全呼び出し元で保証する単一経路）。
//   - null → 既定会議長で補填
//   - end <= start → 日跨ぎ（翌日扱い、MAX_SPAN_MIN 内のみ）。範囲外は補填にフォールバック
//   ※ UI は時刻を HH:MM で round-trip するため日跨ぎ情報が落ちる。ここで再正規化して
//     end < start の不正区間が永続化されるのを防ぐ（S-063-3 の overlap マッチを守る）。
export function resolveEndTime(startIso: string, endIso: string | null): string {
  if (!endIso) return addMinutesToJstIso(startIso, DEFAULT_MEETING_MINUTES);
  if (endIso <= startIso) {
    const nextDay = addDaysIso(endIso, 1);
    const spanMin = (Date.parse(nextDay) - Date.parse(startIso)) / 60000;
    return spanMin > 0 && spanMin < MAX_SPAN_MIN
      ? nextDay
      : addMinutesToJstIso(startIso, DEFAULT_MEETING_MINUTES);
  }
  return endIso;
}

// タグ正規化（来社/外出→訪問、電話→TEL）。
function normalizeTag(raw: string): string {
  switch (raw) {
    case '電話':
      return 'TEL';
    case '来社':
    case '外出':
      return '訪問';
    default:
      return raw; // WEB / 訪問 / TEL / 対面
  }
}

// 会社名正規化（サフィックス除去 + 内部空白圧縮 + 100 字上限）。fuzzy 統合はしない（§3.7）。
export function normalizeCompanyName(raw: string): string {
  let s = (raw || '').trim();
  s = s.replace(COMPANY_SUFFIX, ''); // サフィックス除去
  s = s.replace(/[\s　]+/gu, ' ').trim(); // 内部空白を 1 個に圧縮
  return s.slice(0, 100); // §1 上限 100 字
}

// 時刻抽出（C4 — JST 固定）。括弧内/裸どちらも RE_TIME を全体走査して拾う。
function extractTime(
  body: string,
  baseDateIso: string,
): { startTime: string | null; endTime: string | null; leftover: string } {
  const stripTime = (idx: number, whole: string): string =>
    (body.slice(0, idx) + body.slice(idx + whole.length))
      .replace(/[（(]\s*[）)]/gu, ' ') // 時刻を抜いた後の空括弧を除去
      .replace(/[\s　]+/gu, ' ')
      .trim();

  const range = body.match(RE_TIME);
  if (range) {
    const [whole, sh, sm, eh, em] = range;
    const date = baseDateIso;
    const startTime = `${date}T${pad(sh)}:${pad(sm || '00')}:00+09:00`;
    let endTime: string | null = `${date}T${pad(eh)}:${pad(em || '00')}:00+09:00`;
    // 終了 <= 開始: 「日跨ぎ」と「録音切り忘れ等の異常」を区別する（§3.6 / P1-4）。
    if (endTime <= startTime) {
      const nextDayEnd = addDaysIso(endTime, 1);
      const spanMin = (Date.parse(nextDayEnd) - Date.parse(startTime)) / 60000;
      // 60 分（23:30-0:30）は採用、480 分（23:00-7:00）は不採用 → 補填へ委ねる（テスト #13/#14）。
      endTime = spanMin > 0 && spanMin < MAX_SPAN_MIN ? nextDayEnd : null;
    }
    return { startTime, endTime, leftover: stripTime(range.index || 0, whole) };
  }

  const single = body.match(RE_TIME_SINGLE);
  if (single) {
    const [whole, sh, sm] = single;
    const date = baseDateIso;
    const startTime = `${date}T${pad(sh)}:${pad(sm || '00')}:00+09:00`;
    return { startTime, endTime: null, leftover: stripTime(single.index || 0, whole) };
  }

  return { startTime: null, endTime: null, leftover: body };
}

// 1 行をパースして ParsedEvent を返す（§3.5）。
export function parseLine(rawLine: string, baseDateIso: string): ParsedEvent {
  const original = rawLine;
  let body = normalizeTimeChars(rawLine).trim();
  let needsReview = false;

  // (1) タグ分離
  const tagM = body.match(RE_TAG);
  const tag = tagM ? normalizeTag(tagM[1]) : null;
  if (tagM) body = body.slice(tagM[0].length).trim();

  // (2) 繰返し注記分離
  const recM = body.match(RE_RECUR);
  const recurrence = recM ? recM[1] : null;
  if (recM) body = body.slice(0, recM.index).trim();

  // (3) 時刻抽出（括弧内優先 → 裸）
  const { startTime, endTime, leftover } = extractTime(body, baseDateIso);
  body = leftover.trim();
  if (startTime === null) needsReview = true;

  // (4) 残りの括弧 = 議題候補（時刻でないもの）
  let meetingTitle: string | null = null;
  const parenM = body.match(RE_PAREN);
  if (parenM) {
    meetingTitle = parenM[1].trim() || null;
    body = body.replace(RE_PAREN, ' ').replace(/[\s　]+/gu, ' ').trim();
  }

  // (5) 氏名抽出（末尾）。議題判定・部署判定でガードしてから採用する（§3.5 抽出順序の確定）。
  let contactName = '';
  const nameM = body.match(RE_NAME_TAIL);
  if (nameM && !looksLikeMeetingTitle(nameM[1]) && !looksLikeDept(nameM[1])) {
    let name = nameM[1];
    // 役職・敬称が氏名トークンに食い込んだ場合の除去（部長/課長/さん 等）。2 字以上を残す。
    const hon = name.match(RE_NAME_TITLE_TAIL);
    if (hon && name.length - hon[1].length >= 2) name = name.slice(0, -hon[1].length);
    contactName = name;
    body = body.slice(0, nameM.index).trim();
  }

  // (6) 会社名 / 部署 分割
  let department = '';
  const deptM = body.match(RE_DEPT);
  if (deptM) {
    department = deptM[1].trim();
    body = body.replace(deptM[1], ' ').replace(/[\s　]+/gu, ' ').trim();
  }

  // 部署が取れず、残りが議題っぽいときは「会社名 議題」/「議題のみ」を切り分ける。
  let companyRaw = body;
  if (!department && looksLikeMeetingTitle(body)) {
    const parts = body.split(/[\s　]+/u).filter(Boolean);
    if (parts.length >= 2 && !looksLikeMeetingTitle(parts[0])) {
      // 「会社名 議題」形式: 先頭トークン = 会社名、残り = 議題
      companyRaw = parts[0];
      meetingTitle = meetingTitle ?? parts.slice(1).join(' ');
    } else {
      // 全体が議題のみ（会社名なし）→ 会社名空（でっち上げ禁止 / C1）
      meetingTitle = meetingTitle ?? body;
      companyRaw = '';
    }
  }

  const companyName = normalizeCompanyName(companyRaw);
  if (companyName === '') needsReview = true;

  return {
    companyName,
    department,
    contactName,
    dateIso: baseDateIso, // この行の開始日（呼び出し側が日付見出しで解決した基準日）。
    startTime,
    endTime,
    tag,
    meetingTitle,
    recurrence,
    rawLine: original,
    needsReview,
  };
}

// 生テキスト全体をパースする。会社名も時刻も取れない行は parseErrors に回す（§3.9）。
export function parseCopilotCalendar(rawText: string, baseDateIso: string): ParsedCalendarResult {
  const text = (rawText || '').slice(0, MAX_COPILOT_RAW_CHARS);
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const events: ParsedEvent[] = [];
  const parseErrors: { line: string; reason: string }[] = [];
  // 複数日一括取込: 日付見出し行が現れるまでは既定対象日（baseDateIso）を使い、
  //   見出しを検出するたびに以降の行の基準日を切り替える（§11-3 行ごと日付解決）。
  let currentBaseDate = baseDateIso;
  // 既に見出しを 1 つ以上見たか。最初の見出しは年の繰上げをしない（fallback がピッカー既定日で
  //   時系列の前後関係が無いため。年なし最初の見出しは既定日と同じ年で解釈する）。
  let sawHeader = false;

  for (const line of lines) {
    // 日付のみの見出し行（「2026年6月18日の予定」等）は会社/時刻を持たない。
    //   取込対象外だが、以降の行の基準日として日付を採用する。
    const headerDate = extractDateFromHeader(line, currentBaseDate, sawHeader);
    if (headerDate) {
      currentBaseDate = headerDate;
      sawHeader = true;
      continue;
    }
    // 日付見出しの形だが実在しない日付（13月1日・6月32日 等）は疑似イベント化させず隔離する。
    if (isDateHeaderShaped(line)) {
      parseErrors.push({ line, reason: '日付として解釈できない見出し行です' });
      continue;
    }
    const ev = parseLine(line, currentBaseDate);
    // 核フィールド（会社名・開始時刻）が両方取れない行は「取り込めなかった行」として隔離する。
    if (ev.startTime === null && ev.companyName === '') {
      parseErrors.push({ line, reason: '会社名と時刻を読み取れませんでした' });
    } else {
      events.push(ev);
    }
  }

  return { events, parseErrors, geminiAssisted: false };
}
